import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { getCurrentView } from '@hc/redux-saga-router-plus/hclib/selectors';
import { MarkerPopup } from 'hcmaps-leaflet';

import {
  COMP_TYPE_DEFAULT,
  COMP_TYPE_RENTAL,
  MAP_CONTROL_TYPE_HEATMAP,
  MAP_CONTROL_TYPE_DRAW,
  MAP_ID_BY_COMP_TYPE
} from 'legacy/appstore/constants';
import { VIEWS, COMP_SELECTION_VIEWS } from 'legacy/routes/constants';
import { EVENTS } from 'legacy/appstore/events';

import { propertyTypeToAbbr } from 'legacy/utils/property-types';
import { buildMarker } from 'legacy/utils/maps';

import { toastShow } from 'actions/toast';
import { vrRouteChange } from 'actions/navigation';
import { uiCompsMapControlsToggle } from 'actions/ui';
import { analyticsEvent } from 'actions/analytics';

import {
  getSubjectPriceKey,
  getSubjectAdjusted,
  getSubjectRental,
  getSubjectLocationUnknown,
  getSubjectMarkerClassNames
} from 'selectors/subject';
import {
  getCompsMapMarkersUserSelectedAndAvailable,
  getCompsMapMarkersUserSelected,
  getCompsMapMarkersSelected,
  getCompsFarmUpdatedAt,
  getCompsShowOnMap
} from 'selectors/comps';
import { getCompsListViewSelected, getCompType } from 'selectors/comps-context';
import {
  getRentalCompsMapMarkersUserSelectedAndAvailable,
  getRentalCompsMapMarkersUserSelected,
  getRentalCompsMapMarkersSelected,
  getRentalCompsFarm,
  getRentalCompsFarmUpdatedAt,
  getRentalCompsShowOnMap
} from 'selectors/rental-comps';
import { getMapDrawPolygons, getMapDrawLayerCenter } from 'selectors/map-draw';
import { getMqSmallMediumAndUp, getScreenSize } from 'selectors/match-media';
import { getUiCompsMapVisibleControlType } from 'selectors/ui';
import { getToken } from 'selectors/account-details';
import { getIsLoggedIn } from 'selectors/auth';
import { getIsReportSaveable } from 'selectors/report-permissions';

import PropertyCardContainer from 'legacy/containers/property-card';
import CompsMap from 'legacy/components/CompsMap';

const compSelectorsDefault = {
  getHoveredPin: getCompsShowOnMap,
  getMapMarkersUserSelectedAndAvailable:
    getCompsMapMarkersUserSelectedAndAvailable,
  getMapMarkersUserSelected: getCompsMapMarkersUserSelected,
  getMapMarkersSelected: getCompsMapMarkersSelected,
  getSubject: getSubjectAdjusted,
  getFarmUpdatedAt: getCompsFarmUpdatedAt
};

const compSelectorsRental = {
  getHoveredPin: getRentalCompsShowOnMap,
  getMapMarkersUserSelectedAndAvailable:
    getRentalCompsMapMarkersUserSelectedAndAvailable,
  getMapMarkersUserSelected: getRentalCompsMapMarkersUserSelected,
  getMapMarkersSelected: getRentalCompsMapMarkersSelected,
  getSubject: getSubjectRental,
  getFarmUpdatedAt: getRentalCompsFarmUpdatedAt
};

const DATA_HC_NAMES = {
  [COMP_TYPE_DEFAULT]: {
    map: 'comps-map'
  },
  [COMP_TYPE_RENTAL]: {
    map: 'rental-comps-map'
  }
};

const mapStateToProps = (state, ownProps) => {
  const compType = ownProps.compType || getCompType(state);
  const mapId = MAP_ID_BY_COMP_TYPE[compType];
  const currentView = getCurrentView(state);
  const isViewingSelected = getCompsListViewSelected(state);
  const isCompsSelectionPage = COMP_SELECTION_VIEWS.indexOf(currentView) > -1;
  const isReportPage = !isCompsSelectionPage;
  const compSelectors =
    compType === COMP_TYPE_DEFAULT ? compSelectorsDefault : compSelectorsRental;

  const isMobile = !getMqSmallMediumAndUp(state);
  const priceKey =
    compType === COMP_TYPE_DEFAULT
      ? getSubjectPriceKey(state)
      : 'rentalAvm.priceMean';
  const subjectProperty = compSelectors.getSubject(state);
  const showRentalCompsSection = !isEmpty(getRentalCompsFarm(state));

  let markers = isReportPage
    ? compSelectors.getMapMarkersSelected(priceKey)(state)
    : isViewingSelected
    ? compSelectors.getMapMarkersUserSelected(priceKey)(state)
    : compSelectors.getMapMarkersUserSelectedAndAvailable(priceKey)(state);

  // Provide a marker popup using details from the marker
  const MarkerPopupForMarker = (marker) => {
    return (
      <MarkerPopup
        className="map-marker-popup--comps"
        leafletOptions={{
          autoPanPadding: isMobile ? [20, 20] : [70, 70],
          closeButton: true
        }}
      >
        <PropertyCardContainer
          addressId={marker.comp.addressId}
          compType={compType}
          isSubject={marker.isSubject}
          layout="horizontal"
          hideSelectControl={marker.isSubject}
          hideSimilarity={marker.isSubject}
          noOnClick={marker.isSubject}
        />
      </MarkerPopup>
    );
  };

  const getShowControlsClickAction = (controlType) =>
    isMobile
      ? [
          () =>
            vrRouteChange({
              view: VIEWS.FULL_MAP_VALUE_REPORT,
              params: { maptype: 'comps' }
            })
        ]
      : isCompsSelectionPage
      ? [() => uiCompsMapControlsToggle(mapId, controlType)]
      : [
          () =>
            vrRouteChange({
              view:
                compType === COMP_TYPE_DEFAULT
                  ? VIEWS.COMPS_SELECTION
                  : VIEWS.RENTAL_COMPS_SELECTION
            }),
          () => uiCompsMapControlsToggle(mapId, controlType)
        ];

  const drawLayerPolygons = getMapDrawPolygons(mapId)(state);
  if (
    drawLayerPolygons &&
    drawLayerPolygons.length &&
    (!markers || !markers.length)
  ) {
    // // Center map over the polygon
    markers = [
      buildMarker({
        className: getSubjectMarkerClassNames(state),
        property: { geoLocation: getMapDrawLayerCenter(mapId)(state) },
        isHomeStyle: true,
        iconSize: 'small',
        isSubject: true
      })
    ];
  }

  const locationUnknown = getSubjectLocationUnknown(state);
  return {
    hoveredPin: compSelectors.getHoveredPin(state),
    initialZoom: 12,
    token: getToken(state),
    markers: markers,
    disableScrollZoom: ownProps.disableScrollZoom,
    disableDragging: !ownProps.isFullMap && isMobile,
    // Re-init map viewport whenever a screen breakpoint is passed (i.e. small -> medium)
    fitMapTrigger: `${getScreenSize(state)}_${
      markers.length
    }_${showRentalCompsSection}_${ownProps.fitMapTrigger}`,
    fitMapPadding: ownProps.isHidingControls && {
      topLeft: [25, 50],
      bottomRight: [25, 25]
    },
    referenceProperty: {
      type: propertyTypeToAbbr(subjectProperty.propertyType)
    },
    isHidingControls: ownProps.isHidingControls,
    showControlsClickActionLayers: getShowControlsClickAction(
      MAP_CONTROL_TYPE_HEATMAP
    ),
    showControlsClickActionDraw: getShowControlsClickAction(
      MAP_CONTROL_TYPE_DRAW
    ),
    getMarkerPopup:
      !(isMobile && ownProps.disableMobileMarkerPopup) && MarkerPopupForMarker,
    fullWidthLegend: isMobile,
    useRentalGaiaEndpoint: compType === COMP_TYPE_RENTAL,
    dataHcNames: DATA_HC_NAMES[compType],
    areControlsToggleable: isCompsSelectionPage,
    showToggleControls: !locationUnknown,
    visibleControlsType: getUiCompsMapVisibleControlType(state),
    farmUpdatedAt: compSelectors.getFarmUpdatedAt(state),
    showLoginPromptForDraw: getIsReportSaveable(state) && !getIsLoggedIn(state),
    locationUnknownPosition:
      drawLayerPolygons && drawLayerPolygons.length ? 'bottomRight' : 'center',
    showClusterControl: !isReportPage,
    drawLayerPolygons,
    locationUnknown,
    mapId
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { compType = COMP_TYPE_DEFAULT } = ownProps;
  const mapId = MAP_ID_BY_COMP_TYPE[compType];
  return {
    toastShow: (message) => {
      dispatch(toastShow(message));
    },
    onMarkerClick: (api, marker) => {
      if (marker.fitOnClick) {
        api.setMapPosition([marker.lat, marker.lon]);
      }
    },
    onShowMapControlsClick: (actions) => {
      actions.forEach((action) => dispatch(action()));
    },
    handleMapLayersChange: (layerIds) => {
      dispatch(
        analyticsEvent(EVENTS.MAP_HEATMAP_LAYER, {
          layer_ids: layerIds,
          map_id: mapId
        })
      );
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CompsMap);
